/**
 * 客诉来源（source）UI 聚合规则。
 *
 * 后端 customer_complaint.source 是细粒度枚举（EMAIL/PHONE/SMS/OTHER 等），
 * 但运营关心的是"客服转入"还是"系统自动入库"。UI 侧把它聚合成两档：
 *   - 客服  CUSTOMER_SERVICE  ↔ 后端 EMAIL / PHONE
 *   - 系统  SYSTEM            ↔ 后端 SMS / OTHER
 *
 * 改动这里需同步：
 *   - 列表展示  CustomerComplaintTable.tsx
 *   - 筛选下拉  CustomerComplaintFilter.tsx
 *   - 时间轴节点  CustomerComplaintDetailDrawer.tsx
 *
 * 仅服务于 issueParcelWorkbench 业务模块，不下沉到全局 utils。
 */

/** UI 上对外暴露的聚合分类 */
export type ComplaintSourceFilter = 'CUSTOMER_SERVICE' | 'SYSTEM';

/** UI 分类 → 后端 source 多值，提交筛选时展开 */
export const COMPLAINT_SOURCE_FILTER_TO_BACKEND: Record<ComplaintSourceFilter, string[]> = {
    CUSTOMER_SERVICE: ['EMAIL', 'PHONE'],
    SYSTEM: ['OTHER', 'SMS'],
};

/** 后端 source → UI 分类，列表展示与详情时间轴反查 */
export const COMPLAINT_SOURCE_BACKEND_TO_FILTER: Record<string, ComplaintSourceFilter> = {
    EMAIL: 'CUSTOMER_SERVICE',
    PHONE: 'CUSTOMER_SERVICE',
    SMS: 'SYSTEM',
    OTHER: 'SYSTEM',
};

/**
 * 把后端 source 归类到 UI 聚合分类，未识别值返回 undefined（调用方按需兜底）。
 * 用于列表 source 列、详情时间轴下发节点的操作人区分。
 */
export function resolveComplaintSourceFilter(source?: string): ComplaintSourceFilter | undefined {
    if (!source) return undefined;
    return COMPLAINT_SOURCE_BACKEND_TO_FILTER[source];
}

/**
 * 后端 source → i18n key。
 *   - 命中聚合映射时返回 customerComplaint.source.CUSTOMER_SERVICE / SYSTEM
 *   - 未命中（新增枚举）返回 customerComplaint.source.<原始 code>，由 i18n 文件兜底
 */
export function resolveComplaintSourceI18nKey(source?: string): string | undefined {
    if (!source) return undefined;
    const mapped = COMPLAINT_SOURCE_BACKEND_TO_FILTER[source];
    return `customerComplaint.source.${mapped ?? source}`;
}
